'use client'

import React, { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { GlobalSidebar } from '@/components/ui/global-sidebar'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  Clock,
  Sparkles,
  Loader2,
  Send,
  X,
  ArrowRight,
  Lightbulb,
  AlertCircle,
  Calendar,
  Newspaper,
  FolderOpen,
  Activity,
  User,
  Copy,
  Check,
  ArrowLeft
} from 'lucide-react'
import type { AIResponse, UserData } from '@/lib/types/ai'
import { InlinePostNewsForm } from '@/components/workspace/inline-post-news-form'
import { useTimeTracking } from '@/lib/stores/time-tracking-store'
import { useToast } from '@/hooks/use-toast'
import { useTranslations, useLocale } from 'next-intl'
import { cn } from '@/lib/utils'
import Link from 'next/link'

interface TimeEntryAssistantProps {
  timeEntries: any[];
  userData: UserData;
  hideTimeEntries?: boolean; // When true, hide the time entries section (e.g., on time tracking page)
  isOpen?: boolean; // External control of open state
  onOpenChange?: (open: boolean) => void; // Callback for open state changes
  hideFloatingButton?: boolean; // When true, hide the floating button (use header button instead)
}

export function TimeEntryAssistant({
  timeEntries,
  userData,
  hideTimeEntries = false,
  isOpen: externalIsOpen,
  onOpenChange,
  hideFloatingButton = false
}: TimeEntryAssistantProps) {
  const t = useTranslations('timePage.assistant')
  const locale = useLocale()
  const isRTL = locale === 'ar' || locale === 'ur'
  const activeTimer = useTimeTracking((s) => s.activeTimer)

  const QUICK_SUGGESTIONS = [
    t('suggestions.generateToday'),
    t('suggestions.weeklySummary'),
    t('suggestions.analyzePatterns'),
    t('suggestions.suggestDescriptions')
  ]
  const [assistantRightStyle, setAssistantRightStyle] = useState<string | undefined>(undefined)
  const [freshTimeEntries, setFreshTimeEntries] = useState<any[]>(timeEntries)
  const [loadingTimeEntries, setLoadingTimeEntries] = useState(false)

  // Compute assistant position to sit immediately left of the floating timer
  const computeAssistantPosition = () => {
    try {
      if (typeof window === 'undefined') return
      const timerEl = document.querySelector('[data-floating-timer]') as HTMLElement | null
      if (!timerEl) {
        // Timer not visible - position FAB at bottom right (default position)
        setAssistantRightStyle('24px') // 1.5rem = 24px to match bottom-6
        return
      }

      const rect = timerEl.getBoundingClientRect()
      const spacing = 8 // small gap between timer and assistant
      // We position assistant such that its right edge is (window.innerWidth - rect.left + spacing)
      // But since we set `right` CSS property, compute distance from right edge
      const distanceFromRight = Math.max(window.innerWidth - rect.left + spacing, 16)
      setAssistantRightStyle(`${distanceFromRight}px`)
    } catch (err) {
      // Fallback to default position on error
      setAssistantRightStyle('24px')
    }
  }

  // Recompute on resize and when timer toggles
  useEffect(() => {
    computeAssistantPosition()
    window.addEventListener('resize', computeAssistantPosition)
    return () => window.removeEventListener('resize', computeAssistantPosition)
  }, [activeTimer])
  
  const [internalOpen, setInternalOpen] = useState(false)
  const open = externalIsOpen !== undefined ? externalIsOpen : internalOpen
  const setOpen = (value: boolean) => {
    // Reset state when closing sidebar
    if (!value) {
      setShowPostNewsForm(false)
      setCollapseResponse(false)
      setQuery('')
      setError(null)
      setResponse(null)
      setShowSuggestions(false)
    }
    
    if (onOpenChange) {
      onOpenChange(value)
    } else {
      setInternalOpen(value)
    }
  }

  // Fetch fresh time entries when assistant opens
  useEffect(() => {
    if (open) {
      fetchFreshTimeEntries()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const fetchFreshTimeEntries = async () => {
    setLoadingTimeEntries(true)
    try {
      const { getAuthHeaders } = await import('@/lib/api-utils')
      const authHeaders = getAuthHeaders()
      
      // Fetch time entries from last 7 days for better context
      const today = new Date()
      const sevenDaysAgo = new Date(today)
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7)
      
      // Format dates in local timezone to avoid UTC conversion issues
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      const fromString = `${sevenDaysAgo.getFullYear()}-${String(sevenDaysAgo.getMonth() + 1).padStart(2, '0')}-${String(sevenDaysAgo.getDate()).padStart(2, '0')}`
      
      const params = new URLSearchParams({
        from: fromString,
        to: todayString,
        limit: '100',
        _t: Date.now().toString() // Cache-busting
      })

      const response = await fetch(`/api/time?${params}`, {
        headers: authHeaders,
        cache: 'no-store'
      })

      if (response.ok) {
        const data = await response.json()
        const entries = data.time_entries || []
        
        setFreshTimeEntries(entries)
        
      } else {
  console.error('❌ Failed to fetch time entries:', response.status, response.statusText)
      }
    } catch (err) {
  console.error('❌ Failed to fetch fresh time entries:', err)
      // Fall back to prop data if fetch fails
      setFreshTimeEntries(timeEntries)
    } finally {
      setLoadingTimeEntries(false)
      
    }
  }
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [response, setResponse] = useState<AIResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [showPostNewsForm, setShowPostNewsForm] = useState(false)
  const [collapseResponse, setCollapseResponse] = useState(false)
  const [hideEntriesSection, setHideEntriesSection] = useState(false)
  const [copied, setCopied] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  function extractIssueNumbers(text: string): number[] {
    const issueNumbers: number[] = []
    const hashPattern = /#(\d+)/g
    let match
    while ((match = hashPattern.exec(text)) !== null) {
      issueNumbers.push(parseInt(match[1]))
    }

    const wordPattern = /(?:issue|task|bug|ticket)(?:\s+number|\s+#)?\s+(\d+)/gi
    while ((match = wordPattern.exec(text)) !== null) {
      const num = parseInt(match[1])
      if (!issueNumbers.includes(num)) {
        issueNumbers.push(num)
      }
    }

    return issueNumbers
  }

  function extractKeywords(text: string): string[] {
    const stopWords = ['what', 'when', 'where', 'who', 'why', 'how', 'is', 'are', 'the', 'a', 'an',
                       'show', 'me', 'my', 'find', 'search', 'for', 'about', 'tell', 'give', 'get',
                       'issue', 'task', 'tasks', 'bug', 'ticket', 'work', 'on', 'with', 'in', 'to', 'of',
                       'time', 'entry', 'entries', 'log', 'hours']

    let cleanedText = text.replace(/#\d+/g, '')
    cleanedText = cleanedText.replace(/(?:issue|task|bug|ticket)(?:\s+number|\s+#)?\s+\d+/gi, '')

    const words = cleanedText
      .toLowerCase()
      .replace(/[^\w\s]/g, ' ')
      .split(/\s+/)
      .filter(word => word.length > 2 && !stopWords.includes(word))

    return Array.from(new Set(words))
  }

  async function searchTasksByKeywords(keywords: string[], authHeaders: HeadersInit): Promise<number[]> {
    if (keywords.length === 0) return []

    try {

      const response = await fetch('/api/issues?assigned_to_id=me&limit=100', {
        headers: authHeaders as Record<string, string>
      })

      if (!response.ok) return []

      const data = await response.json()
      const userTasks = data.issues || []

      const matchedTasks = userTasks.filter((task: any) => {
        const subject = (task.subject || '').toLowerCase()
        const description = (task.description || '').toLowerCase()
        const searchText = `${subject} ${description}`
        return keywords.some(keyword => searchText.includes(keyword.toLowerCase()))
      })

      return matchedTasks.map((t: any) => t.id)

    } catch (error) {
      return []
    }
  }

  async function fetchIssuesContext(issueIds: number[], authHeaders: HeadersInit) {
    const issuesWithContext = []

    for (const issueId of issueIds) {
      try {
        const issueRes = await fetch(`/api/issues/${issueId}`, {
          headers: authHeaders as Record<string, string>
        })

        if (!issueRes.ok) continue

        const issueData = await issueRes.json()
        const issue = issueData.issue

        let projectHierarchy = null
        if (issue.project?.id) {
          try {
            const projectRes = await fetch(`/api/projects/${issue.project.id}`, {
              headers: authHeaders as Record<string, string>
            })

            if (projectRes.ok) {
              const projectData = await projectRes.json()
              projectHierarchy = {
                name: projectData.project.name,
                identifier: projectData.project.identifier,
                parent: projectData.project.parent
              }
            }
          } catch (err) {
          }
        }

        issuesWithContext.push({
          id: issue.id,
          subject: issue.subject,
          description: issue.description,
          status: issue.status,
          priority: issue.priority,
          tracker: issue.tracker,
          assigned_to: issue.assigned_to,
          project: issue.project,
          projectHierarchy,
          estimated_hours: issue.estimated_hours,
          spent_hours: issue.spent_hours,
          done_ratio: issue.done_ratio
        })

      } catch (err) {
      }
    }

    return issuesWithContext
  }

  async function handleSubmit(commandQuery?: string) {
    const finalQuery = commandQuery || query
    if (!finalQuery.trim()) return

    // Wait for fresh time entries to load if still loading
    if (loadingTimeEntries) {
      
      // Wait a bit and try again
      setTimeout(() => handleSubmit(commandQuery), 500)
      return
    }

    

    setLoading(true)
    setError(null)
    setShowSuggestions(false)
    setResponse(null)

    try {
      const { getAuthHeaders } = await import('@/lib/api-utils')
      const authHeaders = getAuthHeaders()

      const issueNumbers = extractIssueNumbers(finalQuery)
      const keywords = extractKeywords(finalQuery)

      let keywordMatchedIssueIds: number[] = []
      if (keywords.length > 0) {
        keywordMatchedIssueIds = await searchTasksByKeywords(keywords, authHeaders)
      }

      const allIssueIds = Array.from(new Set([...issueNumbers, ...keywordMatchedIssueIds]))

      // Filter time entries to last 2 days for better context
      const now = new Date()
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      const yesterday = new Date(today)
      yesterday.setDate(yesterday.getDate() - 1)

      // Format today's date in local timezone (YYYY-MM-DD) - avoid UTC conversion
      const todayString = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      
      

      const recentTimeEntries = freshTimeEntries.filter((entry: any) => {
        const entryDate = new Date(entry.spent_on)
        const entryDay = new Date(entryDate.getFullYear(), entryDate.getMonth(), entryDate.getDate())
        const isRecent = entryDay.getTime() >= yesterday.getTime()
        
        return isRecent
      })

      
      
      // Group entries by date for better context
      const entriesByDate = freshTimeEntries.reduce((acc: any, entry: any) => {
        const date = entry.spent_on
        if (!acc[date]) {
          acc[date] = []
        }
        acc[date].push({
          id: entry.id,
          hours: entry.hours,
          comments: entry.comments,
          issue: entry.issue,
          activity: entry.activity
        })
        return acc
      }, {})
      
      // Calculate total hours for today
      const todayEntries = entriesByDate[todayString] || []
      const todayHours = todayEntries.reduce((sum: number, e: any) => sum + (e.hours || 0), 0)
      
      

      let enrichedContext: any = {
        currentPage: 'workspace',
        userId: 'current',
        userData,
        recentActivity: recentTimeEntries,
        allTimeEntries: freshTimeEntries, // Include ALL entries for full context
        entriesByDate, // Grouped by date
        todayStats: {
          date: todayString,
          entriesCount: todayEntries.length,
          totalHours: todayHours
        },
        dateRange: 'last 7 days',
        currentDate: todayString,
        yesterday: yesterday.toISOString().split('T')[0]
      }

      if (allIssueIds.length > 0) {
        const issuesContext = await fetchIssuesContext(allIssueIds, authHeaders)
        enrichedContext = {
          ...enrichedContext,
          mentionedIssues: issuesContext,
          searchKeywords: keywords
        }
      }

      const res = await fetch('/api/assistant/ai', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        body: JSON.stringify({
          userMessage: finalQuery,
          context: enrichedContext,
          assistantType: 'timeAssist',
          source: 'other' // Not homepage chat - don't send to Slack
        })
      })

      if (!res.ok) {
        const errorData = await res.json()
        throw new Error(errorData.error || 'Failed to get time entry assistance')
      }

      const data: AIResponse = await res.json()
      setResponse(data)
    } catch (err: any) {
      setError(err.message || 'Failed to process request')
    } finally {
      setLoading(false)
    }
  }

  function handleSuggestionClick(suggestion: string) {
    setQuery(suggestion)
    handleSubmit(suggestion)
  }

  const extractTimeEntries = (text: string): string | null => {
    // Extract content from code blocks
    const codeBlockMatch = text.match(/```(?:\w+)?\n([\s\S]*?)```/);
    if (codeBlockMatch) {
      return codeBlockMatch[1].trim();
    }
    return null;
  }

  const handleCopyTimeEntries = async () => {
    if (!response?.response) return;

    const timeEntries = extractTimeEntries(response.response);
    if (!timeEntries) {
      toast({
        title: "No time entries found",
        description: "Could not find time entries in the response",
        variant: "destructive"
      });
      return;
    }

    try {
      await navigator.clipboard.writeText(timeEntries);
      setCopied(true);
      toast({
        title: t('copied'),
        description: "Time entries copied to clipboard",
      });
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast({
        title: "Copy failed",
        description: "Failed to copy to clipboard",
        variant: "destructive"
      });
    }
  }

  const totalHoursToday = freshTimeEntries
    .filter((entry: any) => {
      const entryDate = new Date(entry.spent_on)
      const today = new Date()
      return entryDate.toDateString() === today.toDateString()
    })
    .reduce((sum: number, entry: any) => sum + (entry.hours || 0), 0)

  return (
    <>
      {!hideFloatingButton && (
        <Button
          className={`fixed bottom-6 rounded-full w-14 h-14 shadow-lg bg-primary hover:bg-primary/90 z-40 transition-all duration-300`}
          size="icon"
          style={{ right: assistantRightStyle || '24px' }}
          onClick={() => setOpen(true)}
        >
          <div className="relative">
            <Clock className="w-6 h-6 text-white" />
            <Sparkles className="w-3 h-3 text-yellow-300 absolute -top-1 -right-1 animate-pulse" />
          </div>
        </Button>
      )}

      <GlobalSidebar
        isOpen={open}
        onClose={() => setOpen(false)}
        title={t('title')}
        subtitle={t('subtitle')}
        icon={<Clock className="w-5 h-5" />}
        width="lg"
      >
        <div className="space-y-6">
          {/* Input Section - Hide when loading or response is shown */}
          {!loading && !response && (
            <div className="relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 z-10">
                <Sparkles className="w-5 h-5 text-primary" />
              </div>
              <Input
                ref={inputRef}
                type="text"
                placeholder={loadingTimeEntries ? 'Loading time entries...' : t('placeholder')}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                disabled={loadingTimeEntries}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSubmit()
                }}
                className="pl-11 pr-24 h-12 text-base border-2 border-gray-300 dark:border-gray-700 focus:border-primary"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <Badge variant="secondary" className="text-xs hidden sm:inline-flex">
                  ⏎
                </Badge>
                <Button
                  size="sm"
                  onClick={() => handleSubmit()}
                  disabled={!query.trim() || loadingTimeEntries}
                  className="bg-primary hover:bg-primary/90"
                >
                  {loadingTimeEntries ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <Card className="border-2">
              <CardContent className="p-8">
                <div className="flex flex-col items-center justify-center space-y-4">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                  <div className="text-center space-y-2">
                    <p className="font-medium text-foreground">Generating time entries...</p>
                    <p className="text-sm text-muted-foreground">
                      Analyzing your recent work and creating entries
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Suggestions - Only show when no loading and no response */}
          {showSuggestions && !response && !loading && (
            <Card className="border-2">
              <div className="flex items-center justify-between p-3 border-b">
                <div className="flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium text-foreground">{t('quickActions')}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setShowSuggestions(false)}
                  className="h-6 w-6 p-0"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
              <div className="p-2 space-y-1">
                {QUICK_SUGGESTIONS.map((suggestion, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-3 py-2 text-sm text-foreground rounded-md hover:bg-muted transition-colors flex items-center justify-between group"
                  >
                    <span>{suggestion}</span>
                    <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </Card>
          )}

          {/* Response Display */}
          {response && (
            <Accordion 
              type="single" 
              collapsible 
              value={collapseResponse ? "" : "response"}
              onValueChange={(value) => setCollapseResponse(value === "")}
              className="border rounded-lg"
            >
              <AccordionItem value="response" className="border-0">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <div className="flex items-center gap-2 flex-1">
                    <Sparkles className="w-5 h-5 text-primary" />
                    <span className="font-semibold text-foreground">AI Assistant Response</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {extractTimeEntries(response.response) && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={handleCopyTimeEntries}
                        className="h-8 px-2 hover:bg-primary/10 hover:text-primary"
                        title="Copy time entries to clipboard"
                      >
                        {copied ? (
                          <>
                            <Check className={cn('w-4 h-4', isRTL ? 'ml-1' : 'mr-1')} />
                            {t('copied')}
                          </>
                        ) : (
                          <>
                            <Copy className={cn('w-4 h-4', isRTL ? 'ml-1' : 'mr-1')} />
                            {t('copy')}
                          </>
                        )}
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="default"
                      onClick={() => {
                        setShowPostNewsForm(true)
                      }}
                      className="h-8 px-3"
                      title="Post time entries as news"
                    >
                      <Newspaper className="w-4 h-4 mr-1" />
                      Post as News
                    </Button>
                    <AccordionTrigger className="p-0 hover:no-underline" />
                  </div>
                </div>
                
                <AccordionContent className="px-4 pb-4">
                  <div className="prose prose-sm dark:prose-invert max-w-none
                    prose-headings:font-semibold prose-headings:text-foreground
                    prose-p:text-foreground prose-p:leading-relaxed
                    prose-strong:text-foreground prose-strong:font-semibold
                    prose-li:text-foreground prose-li:leading-relaxed
                    prose-ul:text-foreground prose-ol:text-foreground
                    prose-a:text-primary hover:prose-a:text-primary/80 hover:prose-a:no-underline
                    prose-code:text-foreground prose-code:bg-muted/60 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:font-mono prose-code:before:content-[''] prose-code:after:content-['']
                    prose-pre:bg-muted/80 prose-pre:text-foreground prose-pre:border prose-pre:border-border prose-pre:rounded-lg prose-pre:p-4 prose-pre:my-4
                    prose-pre:font-mono prose-pre:text-sm prose-pre:leading-relaxed
                    prose-blockquote:text-foreground prose-blockquote:border-l-primary">
                    <ReactMarkdown 
                      remarkPlugins={[remarkGfm]}
                    >
                      {response.response.replace(/#(\d+)/g, '[#$1](/issues/$1)')}
                    </ReactMarkdown>
                  </div>

                  {response.metadata && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-xs text-muted-foreground">
                        Confidence: {Math.round((response.metadata.confidence || 0) * 100)}%
                      </p>
                    </div>
                  )}
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          )}

          {/* Error Display */}
          {error && (
            <Card className="border-2 border-destructive">
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1">
                    <AlertCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-destructive">Error</p>
                      <p className="text-sm text-muted-foreground mt-1">{error}</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setError(null)
                      setQuery('')
                    }}
                    className="h-7 w-7 p-0 shrink-0"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {!hideTimeEntries && !hideEntriesSection && (
            <>
              <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-primary" />
                      <span className="text-sm font-semibold">Today's Summary</span>
                    </div>
                    <Badge variant="secondary" className="font-mono">
                      {totalHoursToday.toFixed(1)}h
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-3">
                  {freshTimeEntries
                    .filter((entry: any) => {
                      const entryDate = new Date(entry.spent_on)
                      const today = new Date()
                      return entryDate.toDateString() === today.toDateString()
                    })
                    .slice(0, 5)
                    .map((entry: any, idx: number) => (
                      <div
                        key={idx}
                        className="py-3 border-b last:border-0 dark:border-gray-700"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0 space-y-2">
                            {/* Issue/Task - Clickable */}
                            {entry.issue ? (
                              <Link
                                href={`/issues/${entry.issue.id}`}
                                className="font-semibold text-sm hover:underline line-clamp-1 block"
                              >
                                <span className="text-muted-foreground">#{entry.issue.id}</span>{' '}
                                {entry.issue.subject || 'Untitled Issue'}
                              </Link>
                            ) : (
                              <div className="font-semibold text-sm line-clamp-1">
                                {entry.comments || 'Time entry'}
                              </div>
                            )}

                            {/* Project, Activity, and User */}
                            <div className="flex items-center gap-2 flex-wrap text-xs">
                              <Link
                                href={`/projects/${entry.project.id}`}
                                className="flex items-center gap-1 text-muted-foreground hover:underline"
                              >
                                <FolderOpen className="h-3 w-3" />
                                <span>{entry.project.name}</span>
                              </Link>
                              <div className="w-1 h-1 rounded-full bg-muted-foreground/40"></div>
                              <div className="flex items-center gap-1">
                                <Activity className="h-3 w-3 text-muted-foreground" />
                                <span className="text-muted-foreground">
                                  {entry.activity?.name || 'General'}
                                </span>
                              </div>
                              <div className="w-1 h-1 rounded-full bg-muted-foreground/40"></div>
                              <div className="flex items-center gap-1">
                                <User className="h-3 w-3 text-muted-foreground" />
                                <span className="text-muted-foreground">
                                  {entry.user?.name || 'Unknown'}
                                </span>
                              </div>
                            </div>
                          </div>

                          {/* Hours */}
                          <div className="flex-shrink-0 text-right">
                            <div className="text-base font-bold font-mono">
                              {entry.hours}h
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  {timeEntries.filter((entry: any) => {
                    const entryDate = new Date(entry.spent_on)
                    const today = new Date()
                    return entryDate.toDateString() === today.toDateString()
                  }).length === 0 && (
                    <div className="text-center py-6">
                      <Clock className="w-8 h-8 text-muted-foreground mx-auto mb-2 opacity-50" />
                      <p className="text-sm text-muted-foreground">No time entries logged today</p>
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">Recent Entries (Last 7 days)</span>
                    <Badge variant="secondary" className="text-xs">
                      {timeEntries.length} total
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {timeEntries.slice(0, 10).map((entry: any, idx: number) => (
                    <div
                      key={idx}
                      className="py-3 border-b last:border-0 dark:border-gray-800"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0 space-y-2">
                          {/* Issue/Task - Clickable */}
                          {entry.issue ? (
                            <Link
                              href={`/issues/${entry.issue.id}`}
                              className="font-semibold text-sm hover:underline line-clamp-1 block"
                            >
                              <span className="text-muted-foreground">#{entry.issue.id}</span>{' '}
                              {entry.issue.subject || 'Untitled Issue'}
                            </Link>
                          ) : (
                            <div className="font-semibold text-sm line-clamp-1">
                              {entry.comments || 'Time entry'}
                            </div>
                          )}

                          {/* Project, Activity, User, and Date */}
                          <div className="flex items-center gap-2 flex-wrap text-xs">
                            <Link
                              href={`/projects/${entry.project.id}`}
                              className="flex items-center gap-1 text-muted-foreground hover:underline"
                            >
                              <FolderOpen className="h-3 w-3" />
                              <span>{entry.project.name}</span>
                            </Link>
                            <div className="w-1 h-1 rounded-full bg-muted-foreground/40"></div>
                            <div className="flex items-center gap-1">
                              <Activity className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {entry.activity?.name || 'General'}
                              </span>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-muted-foreground/40"></div>
                            <div className="flex items-center gap-1">
                              <User className="h-3 w-3 text-muted-foreground" />
                              <span className="text-muted-foreground">
                                {entry.user?.name || 'Unknown'}
                              </span>
                            </div>
                            <div className="w-1 h-1 rounded-full bg-muted-foreground/40"></div>
                            <span className="text-muted-foreground">
                              {new Date(entry.spent_on).toLocaleDateString()}
                            </span>
                          </div>
                        </div>

                        {/* Hours */}
                        <div className="flex-shrink-0 text-right">
                          <div className="text-base font-bold font-mono">
                            {entry.hours}h
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                  {timeEntries.length === 0 && (
                    <div className="text-center py-8">
                      <Clock className="w-10 h-10 text-muted-foreground mx-auto mb-3 opacity-50" />
                      <p className="text-sm text-muted-foreground mb-2">No time entries found</p>
                      <p className="text-xs text-muted-foreground">
                        Start tracking your time to see insights here
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </GlobalSidebar>

      {/* Nested Post News Sidebar */}
      {showPostNewsForm && response && (
        <GlobalSidebar
          isOpen={showPostNewsForm}
          onClose={() => setShowPostNewsForm(false)}
          title="Post as News"
          subtitle="Share your achievements with the team"
          icon={
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowPostNewsForm(false)}
              className="h-8 w-8 p-0 -ml-2"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
          }
          width="lg"
          showCloseButton={false}
        >
          <InlinePostNewsForm
            insights={response.response || ''}
            timeEntries={timeEntries.filter((entry: any) => {
              const entryDate = new Date(entry.spent_on)
              const today = new Date()
              return entryDate.toDateString() === today.toDateString()
            })}
            onClose={() => setShowPostNewsForm(false)}
            onSuccess={() => setShowPostNewsForm(false)}
            onCloseAll={() => {
              // Close the post news form first
              setShowPostNewsForm(false)
              // Then close the main assistant
              setTimeout(() => {
                setOpen(false)
              }, 100)
            }}
          />
        </GlobalSidebar>
      )}
    </>
  )
}
